import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import ThresholdNote from './ThresholdNote';
import WarningLoading from './WarningLoading';
import apiRequest from '../../api/request';
import FlagSummary from './FlagSummary';
import WarningMetricTable from './WarningMetricTable';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

// ATP - Average Tutor Payment, for students TC-uploaded this period.
// Warns ABOVE threshold (too expensive per session = bad). Employee +
// Project view only - see atp_dashboard.controller.js for the Tutor +
// Project view the document also asks for, not yet built.
const AtpDashboard = () => {
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0 });
  const [note, setNote] = useState(null); // why no row has a threshold (starts later / switched off / never set)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `warning-dashboard/atp-dashboard?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error
        // rendered as an empty page (or zeros) instead of a message
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load ATP results');
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({ totalEvaluated: res?.data?.totalEvaluated || 0, warningCount: res?.data?.warningCount || 0 });
        setNote(res?.data?.thresholdNote || null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load ATP results');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey]);

  const dangerScore = (row) => (row.threshold === null || row.value === null ? -Infinity : row.value - row.threshold);
  const sortedRows = useMemo(() => [...rows].sort((a, b) => dangerScore(b) - dangerScore(a)), [rows]);

  const columns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell isWarning={row.isWarning} />, csvValue: (row) => (row.isWarning ? 'Warning' : 'OK') },
    {
      field: 'entityName',
      headerName: 'Name',
      render: (row) => (
        <Typography variant="body2" fontStyle={row.isActive === false ? 'italic' : 'normal'} color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}>
          {row.entityName}{row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    { field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) },
    { field: 'totalTutorPayments', headerName: 'Tutor Payments', align: 'right', render: (row) => `₹${Number(row.totalTutorPayments).toLocaleString()}`, csvValue: (row) => row.totalTutorPayments },
    { field: 'completedSessions', headerName: 'Completed Sessions', align: 'right' },
    {
      field: 'value',
      headerName: 'ATP',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" fontWeight={row.isWarning ? 700 : 400} color={row.isWarning ? 'error.main' : 'text.primary'}>
          {row.value === null ? '-' : `₹${Number(row.value).toLocaleString()}`}
        </Typography>
      ),
      csvValue: (row) => (row.value === null ? '-' : row.value),
    },
    {
      field: 'threshold',
      headerName: 'Threshold',
      align: 'right',
      render: (row) => (row.threshold === null ? <Typography variant="body2" color="text.secondary">{row.thresholdOff ? 'Switched off' : 'Not configured'}</Typography> : `> ₹${Number(row.threshold).toLocaleString()}`),
      csvValue: (row) => (row.threshold === null ? (row.thresholdOff ? 'Switched off' : 'Not configured') : `> ₹${Number(row.threshold).toLocaleString()}`),
    },
  ];

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title="ATP - Average Tutor Payment"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">Back to Warning Dashboard</Button>
          {canConfigure('project-threshold-config') && (
            <Button component={Link} to={`/dashboard/warning-dashboard/project-threshold-config?month=${month.format('YYYY-MM-DD')}`} variant="outlined">Configure Thresholds</Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}
      {!loading && !error && <ThresholdNote note={note} month={month} configPath="project-threshold-config" canSet={canConfigure('project-threshold-config')} />}
      {!loading && !error && (
        <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.totalEvaluated} />
      )}
      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage="No students were TC-uploaded this period."
          exportFileName="ATP"
          rowKey={(row) => row.entityId}
        />
      )}
    </WarningPage>
  );
};

export default AtpDashboard;
